import { useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import { Button } from '@/components/ui/Button'
import { ErrorMessage, OfflineBanner } from '@/components/ui/ErrorMessage'
import { EducationalLoader } from '@/components/analysis/EducationalLoader'
import { GuidedCameraScreen } from '@/components/camera/GuidedCameraScreen'
import { analyzeSkinPhoto, scoresToConcerns } from '@/services/perfectcorp'
import { uploadPhoto, retrievePhoto } from '@/services/photoStorage'
import { classifyError, isOnline, type AnalysisError } from '@/services/errors'
import { trackFunnelEvent, trackEngagementEvent, trackError } from '@/services/analytics'
import { useAnalysisStore } from '@/store/analysisStore'
import { useSurveyStore } from '@/store/surveyStore'
import { useAuthStore } from '@/store/authStore'
// import { useToastStore } from '@/store/toastStore'  // Unused - S3 upload errors are now silent
import { mockAnalysisResults } from '@/data/mockAnalysis'
import { TEST_TEASER_RESULTS, TEST_PHOTO_ID } from '@/lib/testFixtures'
import { debugLog } from '@/lib/logger'

// Local test photo path - using test-shane.jpg (Shane's face for dev testing)
const TEST_PHOTO_LOCAL_PATH = '/image/test-shane.jpg'
import type { SkinType } from '@/types/analysis'

// MOCK MODE: Set to true to skip PerfectCorp API and use mock data
// This allows testing the full UX flow without API calls
const USE_MOCK_DATA = false

type UploadState = 'guide' | 'camera' | 'crop' | 'preview' | 'uploading' | 'error'

// Maximum retry attempts for analysis
const MAX_RETRIES = 2

// Navigation state for photo retake flows
interface PhotoNavigationState {
  returnTo?: string        // '/full-survey' - return after capture
  isRetake?: boolean       // true - from FullSurvey intro
  isReportRetake?: boolean // true - from ReportPage
}

export function PhotoUploadPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const nativeCameraInputRef = useRef<HTMLInputElement>(null)

  // Check for navigation state from retake flows
  const navigationState = location.state as PhotoNavigationState | null
  const returnTo = navigationState?.returnTo
  const isRetake = navigationState?.isRetake
  const isReportRetake = navigationState?.isReportRetake

  // Detect iOS to use native camera capture instead of getUserMedia
  // iOS Safari has issues with large data URLs and canvas operations
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent)

  // Stores
  const { analysisResults, setAnalysisResults, setError: setStoreError, setPhotoId } = useAnalysisStore()
  const { shortSurvey } = useSurveyStore()
  const { hasPaid, demoMode } = useAuthStore()

  const [state, setState] = useState<UploadState>('guide')

  // Auto-redirect users with existing results (only on initial page load, not during photo flow)
  // SKIP auto-redirect if user is in retake mode (coming from FullSurvey or Report)
  const hasRedirected = useRef(false)
  useEffect(() => {
    // Skip redirect if in retake mode - user intentionally wants to take new photo
    if (isRetake || isReportRetake) {
      return
    }
    // Only redirect if on initial 'guide' state - don't interfere with active photo flow
    if (analysisResults && !hasRedirected.current && state === 'guide') {
      hasRedirected.current = true
      if (hasPaid || demoMode) {
        navigate('/report', { state: { showWelcomeToast: true } })
      } else {
        navigate('/teaser', { state: { showWelcomeToast: true } })
      }
    }
  }, [analysisResults, hasPaid, demoMode, navigate, state, isRetake, isReportRetake])
  // Store Blobs instead of data URLs to avoid iOS Safari size limits
  const [rawPhotoBlob, setRawPhotoBlob] = useState<Blob | null>(null)
  const [rawPhotoUrl, setRawPhotoUrl] = useState<string | null>(null)  // Blob URL for display
  const [croppedPhotoBlob, setCroppedPhotoBlob] = useState<Blob | null>(null)
  const [croppedPhotoUrl, setCroppedPhotoUrl] = useState<string | null>(null)  // Blob URL for display
  const [error, setError] = useState<string | null>(null)
  const [analysisError, setAnalysisError] = useState<AnalysisError | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const [isRetrying, setIsRetrying] = useState(false)
  const [isOffline, setIsOffline] = useState(!isOnline())
  const [analysisProgress, setAnalysisProgress] = useState(0)  // 0-100 for EducationalLoader

  // Crop state
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [isRetakeMode, setIsRetakeMode] = useState(false)  // Track if retaking from preview

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  // Track URLs in refs for cleanup on unmount (avoids stale closure issues)
  const rawPhotoUrlRef = useRef<string | null>(null)
  const croppedPhotoUrlRef = useRef<string | null>(null)

  // Keep refs in sync with state
  useEffect(() => {
    rawPhotoUrlRef.current = rawPhotoUrl
  }, [rawPhotoUrl])

  useEffect(() => {
    croppedPhotoUrlRef.current = croppedPhotoUrl
  }, [croppedPhotoUrl])

  // Cleanup Blob URLs only on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (rawPhotoUrlRef.current) URL.revokeObjectURL(rawPhotoUrlRef.current)
      if (croppedPhotoUrlRef.current) URL.revokeObjectURL(croppedPhotoUrlRef.current)
    }
  }, [])

  // Offline detection
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const handleOpenCamera = () => {
    setError(null)
    trackFunnelEvent.photoSourceSelected('camera')
    // Use custom camera for all platforms (better UX)
    // The issue is NOT in camera capture, it's in image processing
    setState('camera')
  }

  // Handle native camera capture (iOS)
  const handleNativeCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      debugLog('[PhotoUpload] Native camera file received:', file.name, file.size, file.type)
      if (!file.type.startsWith('image/')) {
        setError('Please take a photo')
        return
      }
      // File is already a JPEG from iOS camera - use it directly
      // No canvas processing = no iOS Safari size limits
      handlePhotoCapture(file)
    }
    e.target.value = ''
  }

  const handlePhotoCapture = async (blob: Blob) => {
    // Clear any previous errors
    setError(null)

    // Revoke old URLs if they exist
    if (rawPhotoUrl) {
      URL.revokeObjectURL(rawPhotoUrl)
    }
    if (croppedPhotoUrl) {
      URL.revokeObjectURL(croppedPhotoUrl)
    }

    debugLog('[PhotoUpload] handlePhotoCapture called, blob size:', Math.round(blob.size / 1024), 'KB, type:', blob.type)

    try {
      // On iOS, we need to be VERY aggressive with image sizes
      // The error "String did not match expected pattern" occurs when iOS Safari
      // tries to decode large images - even with blob URLs
      //
      // Strategy: Resize to a VERY safe size (1600px) that iOS can definitely handle
      // This is still above PerfectCorp's 1536px minimum requirement
      const MAX_DIMENSION = isIOS ? 1600 : 4096

      debugLog('[PhotoUpload] Starting resize, isIOS:', isIOS, 'maxDim:', MAX_DIMENSION)

      const resizedBlob = await resizeImageForIOSSafely(blob, MAX_DIMENSION)

      debugLog('[PhotoUpload] Resize complete, new size:', Math.round(resizedBlob.size / 1024), 'KB')

      setRawPhotoBlob(resizedBlob)

      // Create blob URL for display
      const blobUrl = URL.createObjectURL(resizedBlob)
      debugLog('[PhotoUpload] Blob URL created:', blobUrl.substring(0, 50))

      setRawPhotoUrl(blobUrl)

      // Always show the cropper for all platforms
      // Front camera produces smaller images, safe for iOS now
      setCrop({ x: 0, y: 0 })
      setZoom(1)
      setRotation(0)

      debugLog('[PhotoUpload] Going to crop screen')
      setState('crop')
      debugLog('[PhotoUpload] setState to crop complete')
    } catch (err) {
      console.error('[PhotoUpload] Failed to process photo:', err)
      const errMsg = err instanceof Error ? `${err.name}: ${err.message}` : 'Unknown error'
      const stack = err instanceof Error ? err.stack : ''
      console.error('[PhotoUpload] Stack:', stack)
      setError(`Failed to process photo: ${errMsg}`)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file')
        return
      }
      trackFunnelEvent.photoSourceSelected('gallery')
      // File is already a Blob - use it directly
      handlePhotoCapture(file)
    }
    e.target.value = ''
  }

  const handleCropConfirm = async () => {
    if (!rawPhotoUrl || !croppedAreaPixels) return

    try {
      // getCroppedImg now returns a Blob, with rotation support
      const croppedBlob = await getCroppedImg(rawPhotoUrl, croppedAreaPixels, rotation)
      // Revoke old URL if exists
      if (croppedPhotoUrl) {
        URL.revokeObjectURL(croppedPhotoUrl)
      }
      setCroppedPhotoBlob(croppedBlob)
      const blobUrl = URL.createObjectURL(croppedBlob)
      setCroppedPhotoUrl(blobUrl)
      debugLog('[PhotoUpload] Cropped, blob size:', Math.round(croppedBlob.size / 1024), 'KB')
      setState('preview')
    } catch (err) {
      console.error('[PhotoUpload] Crop failed:', err)
      setError('Failed to crop image')
    }
  }

  const handleRetake = () => {
    // Clean up Blob URLs
    if (rawPhotoUrl) URL.revokeObjectURL(rawPhotoUrl)
    if (croppedPhotoUrl) URL.revokeObjectURL(croppedPhotoUrl)
    setRawPhotoBlob(null)
    setRawPhotoUrl(null)
    setCroppedPhotoBlob(null)
    setCroppedPhotoUrl(null)
    setError(null)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setRotation(0)
    setIsRetakeMode(false)
    setState('guide')
  }

  // Retake from preview - goes directly to camera and preserves previous photo on cancel
  const handleRetakeFromPreview = () => {
    trackEngagementEvent.photoRetake('preview')
    setIsRetakeMode(true)
    setState('camera')
  }

  // Handle camera cancel - preserves photo if in retake mode
  const handleCameraCancel = () => {
    if (isRetakeMode && croppedPhotoUrl) {
      // In retake mode with existing photo - go back to preview
      setIsRetakeMode(false)
      setState('preview')
    } else {
      // Not in retake mode - go back to guide
      setState('guide')
    }
  }

  const handleBackToCrop = () => {
    // Clean up cropped Blob URL
    if (croppedPhotoUrl) URL.revokeObjectURL(croppedPhotoUrl)
    setCroppedPhotoBlob(null)
    setCroppedPhotoUrl(null)
    setState('crop')
  }

  // Retake upload function - uploads photo to S3 only, no API analysis
  // Used when user retakes photo from FullSurvey intro (paid user)
  // HD analysis will happen on CelebrationPage after survey
  const runRetakeUpload = async () => {
    if (!croppedPhotoBlob) return

    setState('uploading')
    setAnalysisProgress(0)
    setAnalysisError(null)

    try {
      debugLog('[K-Skin] Retake mode: uploading photo to S3 only (no API call)')

      // Simulate brief upload progress
      setAnalysisProgress(20)

      // Upload photo to S3
      const newPhotoId = await uploadPhoto(croppedPhotoBlob)
      setPhotoId(newPhotoId)
      debugLog('[K-Skin] Photo uploaded to S3, photoId:', newPhotoId)

      setAnalysisProgress(60)

      // Convert photo to data URL for persistent storage
      const photoDataUrl = await blobToDataURL(croppedPhotoBlob)

      setAnalysisProgress(80)

      // Update only the photoUrl in analysisResults (keep existing metrics)
      // HD analysis will run on CelebrationPage with the new photo
      const currentResults = useAnalysisStore.getState().analysisResults
      if (currentResults) {
        setAnalysisResults({
          ...currentResults,
          photoUrl: photoDataUrl,
          analyzedAt: new Date().toISOString(),
        })
      }

      setAnalysisProgress(100)

      debugLog('[K-Skin] Retake upload complete, navigating to:', returnTo)

      // Navigate back to full survey (will show intro with new photo)
      if (returnTo) {
        navigate(returnTo, { state: { photoRetaken: true } })
      } else {
        // Fallback - should not happen
        navigate('/full-survey', { state: { photoRetaken: true } })
      }
    } catch (err) {
      console.error('[K-Skin] Retake upload failed:', err)
      const classifiedError = classifyError(err)
      setAnalysisError(classifiedError)
      setState('error')
    }
  }

  // Core analysis function - can be called for initial attempt or retry
  const runAnalysis = async (isRetry = false) => {
    if (!croppedPhotoBlob || !rawPhotoBlob) return

    if (isRetry) {
      setIsRetrying(true)
      setRetryCount(prev => prev + 1)
    }

    setState('uploading')
    setAnalysisProgress(0)  // Reset progress
    setAnalysisError(null)
    const analysisStartTime = Date.now()
    trackFunnelEvent.photoAnalysisStarted()

    try {
      // MOCK MODE: Skip API and use mock data for UX testing
      if (USE_MOCK_DATA) {
        debugLog('[K-Skin] MOCK MODE: Using mock data instead of API')

        // Simulate realistic loading sequence with progress
        setAnalysisProgress(10)
        await new Promise(r => setTimeout(r, 800))
        setAnalysisProgress(25)
        await new Promise(r => setTimeout(r, 600))
        setAnalysisProgress(50)
        await new Promise(r => setTimeout(r, 1200))
        setAnalysisProgress(85)
        await new Promise(r => setTimeout(r, 400))
        setAnalysisProgress(100)

        // Use mock data with the captured photo
        setAnalysisResults({
          ...mockAnalysisResults,
          photoUrl: croppedPhotoUrl || '',
          analyzedAt: new Date().toISOString(),
        })

        // Navigate based on flow type (same as real flow)
        if (returnTo && isRetake) {
          navigate(returnTo, { state: { photoRetaken: true } })
        } else if (isReportRetake) {
          navigate('/analysis-complete')
        } else {
          navigate('/teaser')
        }
        return
      }

      debugLog('[K-Skin] Starting analysis...')

      // IMPORTANT: Send the CROPPED photo to the API, not the raw photo!
      // This ensures heatmaps align with the displayed cropped image.
      // The cropped photo was already resized to meet PerfectCorp's minimum resolution
      // requirements in getCroppedImg (min 1536px height).
      const preparedBlob: Blob = croppedPhotoBlob
      debugLog('[K-Skin] Using cropped photo for API, size:', Math.round(croppedPhotoBlob.size / 1024), 'KB')

      const fileSizeKB = Math.round(preparedBlob.size / 1024)
      debugLog('[K-Skin] Blob size:', preparedBlob.size, 'type:', preparedBlob.type)
      const file = new File([preparedBlob], 'selfie.jpg', { type: 'image/jpeg' })
      debugLog('[K-Skin] File created, size:', file.size, '(', fileSizeKB, 'KB )')

      // Upload photo to S3 for later HD analysis (after payment)
      setAnalysisProgress(10)
      try {
        const photoId = await uploadPhoto(preparedBlob)
        setPhotoId(photoId)
        debugLog('[K-Skin] Photo uploaded to S3, photoId:', photoId)
      } catch (uploadError) {
        // S3 upload failure is non-fatal - continue with analysis
        // User can still see teaser results, HD analysis will prompt for new photo if needed
        console.warn('[K-Skin] S3 upload failed (non-fatal):', uploadError)
        // Don't show toast - this is expected during development/staging
        // The teaser flow will still work, user can take new photo for full analysis
      }

      // Call PerfectCorp API with progress updates
      debugLog('[K-Skin] Calling analyzeSkinPhoto...')
      setAnalysisProgress(20)
      const apiResult = await analyzeSkinPhoto(file, (stage, progress) => {
        debugLog('[K-Skin] Progress:', stage, progress)
        // Map API progress (0-100) to our range (20-90)
        if (progress !== undefined) {
          setAnalysisProgress(20 + Math.round(progress * 0.7))
        }
      })
      setAnalysisProgress(95)
      debugLog('[K-Skin] API result received:', apiResult?.skinAge, apiResult?.skinScore)

      // Convert PerfectCorp scores to concern scores (higher = worse)
      const concerns = scoresToConcerns(apiResult.scores)
      debugLog('[K-Skin] Raw API scores:', {
        wrinkles: apiResult.scores.wrinkles,
        pores: apiResult.scores.pores,
        texture: apiResult.scores.texture,
        redness: apiResult.scores.redness,
      })
      debugLog('[K-Skin] Converted concerns:', {
        wrinkles: concerns.wrinkles,
        pores: concerns.pores,
        texture: concerns.texture,
        redness: concerns.redness,
      })

      // Derive skin type from oiliness score
      const deriveSkinType = (): SkinType => {
        const oiliness = concerns.oiliness
        if (oiliness >= 70) return 'oily'
        if (oiliness >= 40) return 'combination'
        if (oiliness <= 20) return 'dry'
        return 'normal'
      }

      // Map to AnalysisResults format
      // Use survey skin type if valid, otherwise derive from analysis
      const skinType: SkinType =
        shortSurvey.skinType && shortSurvey.skinType !== 'not-sure'
          ? shortSurvey.skinType as SkinType
          : deriveSkinType()

      // Debug: Log all available heatmap keys to diagnose mapping issues
      const allHeatmapKeys = Object.keys(apiResult.heatmaps)
      debugLog('[K-Skin] Available heatmap keys:', allHeatmapKeys)
      debugLog('[K-Skin] Heatmap keys detail:', JSON.stringify(allHeatmapKeys, null, 2))

      // Convert cropped photo to data URL for persistent storage
      // Blob URLs get revoked when navigating away, so we need a data URL
      debugLog('[K-Skin] Converting cropped photo to data URL for storage...')
      const photoDataUrl = await blobToDataURL(croppedPhotoBlob)
      debugLog('[K-Skin] Photo data URL created, length:', photoDataUrl.length)

      // Helper to find heatmap by multiple possible keys (SD vs HD naming)
      // SD API may return keys like: pore, pore_whole, pore_all, texture, texture_whole, etc.
      // HD API returns keys like: pore_whole, pore_all, hd_pore_whole, etc.
      const findHeatmap = (baseName: string): string => {
        // Try many possible variations to handle different API response formats
        const variations = [
          // SD format variations (no hd_ prefix)
          `${baseName}`,                    // e.g., "pore"
          `${baseName}_whole`,              // e.g., "pore_whole"
          `${baseName}_all`,                // e.g., "pore_all"
          `${baseName}_output`,             // e.g., "pore_output"
          `${baseName}_output_whole`,       // e.g., "pore_output_whole"
          `${baseName}_output_all`,         // e.g., "pore_output_all"
          // HD format variations (hd_ prefix)
          `hd_${baseName}`,                 // e.g., "hd_pore"
          `hd_${baseName}_whole`,           // e.g., "hd_pore_whole"
          `hd_${baseName}_all`,             // e.g., "hd_pore_all"
        ]

        for (const key of variations) {
          if (apiResult.heatmaps[key]) {
            debugLog(`[K-Skin] Found heatmap for ${baseName} at key: ${key}`)
            return apiResult.heatmaps[key]
          }
        }

        // Last resort: search for any key containing the base name
        const partialMatch = allHeatmapKeys.find(k => k.toLowerCase().includes(baseName.toLowerCase()))
        if (partialMatch && apiResult.heatmaps[partialMatch]) {
          debugLog(`[K-Skin] Found heatmap for ${baseName} via partial match: ${partialMatch}`)
          return apiResult.heatmaps[partialMatch]
        }

        debugLog(`[K-Skin] No heatmap found for ${baseName}`)
        return ''
      }

      setAnalysisResults({
        skinAge: apiResult.skinAge,
        skinScore: apiResult.skinScore,
        skinType,
        metrics: {
          // Core metrics (concern scores: 0-100, higher = worse)
          pores: concerns.pores,
          texture: concerns.texture,
          pigmentation: concerns.spots,
          redness: concerns.redness,
          wrinkles: concerns.wrinkles,
          brightness: apiResult.scores.radiance, // Legacy field
          oiliness: concerns.oiliness,
          acne: concerns.acne,
          // HD metrics (concern scores: 0-100, higher = worse)
          moisture: concerns.moisture,
          firmness: concerns.firmness,
          radiance: concerns.radiance,
          spots: concerns.spots,
          darkCircles: concerns.darkCircles,
          eyeBags: concerns.eyeBags,
          droopyEyelidUpper: concerns.droopyEyelidUpper,
          droopyEyelidLower: concerns.droopyEyelidLower,
        },
        heatmaps: {
          // Use base names - findHeatmap will try all variations
          pores: findHeatmap('pore'),
          texture: findHeatmap('texture'),
          pigmentation: findHeatmap('age_spot') || findHeatmap('spot'),
          wrinkles: findHeatmap('wrinkle'),
          redness: findHeatmap('redness'),
          // HD heatmaps
          acne: findHeatmap('acne'),
          oiliness: findHeatmap('oiliness'),
          moisture: findHeatmap('moisture'),
          firmness: findHeatmap('firmness'),
          radiance: findHeatmap('radiance'),
          darkCircles: findHeatmap('dark_circle'),
          eyeBags: findHeatmap('eye_bag'),
        },
        photoUrl: photoDataUrl,  // Use data URL, not blob URL (blob URLs get revoked on navigation)
        analyzedAt: new Date().toISOString(),
      })

      // Reset retry count on success
      setRetryCount(0)
      setAnalysisProgress(100)

      // Save teaser analysis to backend (non-blocking)
      // Use getState() to access the updated store after setAnalysisResults
      useAnalysisStore.getState().saveToBackend('teaser')

      // Track successful analysis
      trackFunnelEvent.photoAnalysisCompleted({
        duration_ms: Date.now() - analysisStartTime,
        skin_score: apiResult.skinScore,
      })

      // Navigate based on flow type
      if (returnTo && isRetake) {
        // Pre-survey retake: return to full survey with photoRetaken flag
        navigate(returnTo, { state: { photoRetaken: true } })
      } else if (isReportRetake) {
        // Report retake: go straight to HD analysis (skip teaser)
        navigate('/analysis-complete')
      } else {
        // Normal flow: navigate to teaser page
        navigate('/teaser')
      }
    } catch (err) {
      console.error('[K-Skin] Analysis failed:', err)

      // Classify the error for user-friendly display
      const classifiedError = classifyError(err)
      console.error('[K-Skin] Classified error:', classifiedError)

      // Track the failure
      trackFunnelEvent.photoAnalysisFailed(classifiedError.type)
      trackError.analysis(classifiedError.type, classifiedError.message)

      // Store in the analysis store for potential later reference
      setStoreError(classifiedError.technicalDetails || classifiedError.message)

      // Set the error state for display
      setAnalysisError(classifiedError)
      setState('error')
    } finally {
      setIsRetrying(false)
    }
  }

  const handleContinue = async () => {
    // Reset retry count for fresh analysis
    setRetryCount(0)

    // For retake from FullSurvey intro (paid user):
    // Only upload to S3, skip API analysis
    // HD analysis will run on CelebrationPage after survey
    if (isRetake && returnTo) {
      await runRetakeUpload()
    } else {
      // Normal flow or report retake: run full SD analysis
      await runAnalysis(false)
    }
  }

  const handleRetry = async () => {
    if (retryCount >= MAX_RETRIES) {
      // Already at max retries, just show the error
      return
    }
    await runAnalysis(true)
  }

  const handleTakeSelfieFromError = () => {
    trackEngagementEvent.photoRetake('error')
    // Clear error state and open camera directly
    setAnalysisError(null)
    setRetryCount(0)
    // Clean up old photos
    if (rawPhotoUrl) URL.revokeObjectURL(rawPhotoUrl)
    if (croppedPhotoUrl) URL.revokeObjectURL(croppedPhotoUrl)
    setRawPhotoBlob(null)
    setRawPhotoUrl(null)
    setCroppedPhotoBlob(null)
    setCroppedPhotoUrl(null)
    setError(null)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setRotation(0)
    // Go directly to camera
    setState('camera')
  }

  const handleChooseFromGalleryFromError = () => {
    // Clear error state and open file picker
    setAnalysisError(null)
    setRetryCount(0)
    // Clean up old photos
    if (rawPhotoUrl) URL.revokeObjectURL(rawPhotoUrl)
    if (croppedPhotoUrl) URL.revokeObjectURL(croppedPhotoUrl)
    setRawPhotoBlob(null)
    setRawPhotoUrl(null)
    setCroppedPhotoBlob(null)
    setCroppedPhotoUrl(null)
    setError(null)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setRotation(0)
    // Open file picker
    fileInputRef.current?.click()
  }

  const handleBack = () => {
    if (state === 'camera') {
      setState('guide')
    } else if (state === 'crop') {
      handleRetake()
    } else if (state === 'preview') {
      handleBackToCrop()
    } else if (state === 'error') {
      // From error, go back to preview to try again
      setState('preview')
    } else {
      navigate('/survey')
    }
  }

  // Test mode: Use pre-saved analysis data with real test photo from S3
  const handleTestMode = async () => {
    debugLog('[K-Skin] Test mode activated - fetching real test photo from S3')
    setState('uploading')
    setAnalysisProgress(10)

    try {
      // Fetch actual test photo from S3
      setAnalysisProgress(20)
      let photoUrl: string

      try {
        const photoBlob = await retrievePhoto(TEST_PHOTO_ID)
        setAnalysisProgress(40)
        // Convert blob to data URL
        photoUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(photoBlob)
        })
      } catch (fetchError) {
        // Fallback to local photo if S3 fetch fails
        console.warn('[K-Skin] Failed to fetch test photo from S3, using fallback:', fetchError)
        photoUrl = TEST_PHOTO_LOCAL_PATH
      }

      setAnalysisProgress(60)

      // Use pre-saved test analysis results with real photo URL
      const testResults = {
        ...TEST_TEASER_RESULTS,
        photoUrl,
        analyzedAt: new Date().toISOString(),
      }

      setAnalysisProgress(80)

      // Set photoId for later HD analysis (will also use test data)
      setPhotoId(TEST_PHOTO_ID)

      // Store the test results
      setAnalysisProgress(90)
      setAnalysisResults(testResults)

      // Skip backend save in test mode to avoid API calls
      // useAnalysisStore.getState().saveToBackend('teaser')

      setAnalysisProgress(100)

      // Navigate to teaser page
      navigate('/teaser')
    } catch (err) {
      console.error('[K-Skin] Test mode failed:', err)
      setError('Test mode failed. Please try again.')
      setState('guide')
    }
  }

  const getHeaderTitle = () => {
    switch (state) {
      case 'camera':
        return 'Take Your Selfie'
      case 'crop':
        return 'Position Your Face'
      case 'preview':
        return 'Review Photo'
      case 'uploading':
        return 'Analyzing'
      case 'error':
        return 'Analysis Issue'
      default:
        return 'Take Your Photo'
    }
  }

  return (
    <div className="h-dvh bg-white flex flex-col touch-none overflow-hidden">
      {/* Offline Banner */}
      <AnimatePresence>
        {isOffline && <OfflineBanner />}
      </AnimatePresence>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={handleBack} className="text-gray-600 hover:text-gray-900">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-semibold text-gray-900">{getHeaderTitle()}</h1>
          <div className="w-6" />
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex flex-col">
        <AnimatePresence mode="wait">
          {state === 'guide' && (
            <GuideScreen
              onOpenCamera={handleOpenCamera}
              onSelectFile={() => fileInputRef.current?.click()}
              onTestMode={handleTestMode}
              error={error}
            />
          )}

          {state === 'camera' && (
            <GuidedCameraScreen
              onCapture={handlePhotoCapture}
              onCancel={handleCameraCancel}
              onError={(msg) => {
                setError(msg)
                setState('guide')
              }}
            />
          )}

          {state === 'crop' && rawPhotoUrl && (
            <CropScreen
              image={rawPhotoUrl}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              onConfirm={handleCropConfirm}
              onRetake={handleRetake}
            />
          )}

          {state === 'preview' && croppedPhotoUrl && (
            <PreviewScreen
              photoData={croppedPhotoUrl}
              onRetake={handleRetakeFromPreview}
              onAdjust={handleBackToCrop}
              onContinue={handleContinue}
              error={error}
              continueButtonText={isRetake ? 'Confirm' : 'Analyze My Skin'}
            />
          )}

          {state === 'uploading' && <EducationalLoader progress={analysisProgress} />}

          {state === 'error' && analysisError && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col justify-center"
            >
              <ErrorMessage
                error={analysisError}
                onRetry={analysisError.canRetry ? handleRetry : undefined}
                onTakeSelfie={handleTakeSelfieFromError}
                onChooseFromGallery={handleChooseFromGalleryFromError}
                isRetrying={isRetrying}
                retryCount={retryCount}
                maxRetries={MAX_RETRIES}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Hidden file input - opens photo gallery */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Hidden native camera input for iOS - uses front camera directly */}
      {/* capture="user" opens the front camera on iOS for selfie */}
      {/* This bypasses getUserMedia + canvas processing entirely */}
      <input
        ref={nativeCameraInputRef}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        onChange={handleNativeCameraCapture}
      />
    </div>
  )
}

// Decorative sparkle component
const Sparkle = ({ className, size = 24, color = '#FF8C33', delay = 0 }: { className?: string; size?: number; color?: string; delay?: number }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    animate={{
      scale: [1, 1.3, 1],
      opacity: [0.7, 1, 0.7],
      rotate: [0, 15, 0]
    }}
    transition={{
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay
    }}
  >
    <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
  </motion.svg>
)

// Photo tip card data
const PHOTO_TIPS = [
  {
    icon: '📷',
    title: 'Clean your lens',
    description: 'Wipe your front camera for clear photos',
  },
  {
    icon: '🧴',
    title: 'No makeup',
    description: 'Bare skin gives the most accurate results',
  },
  {
    icon: '💡',
    title: 'Good lighting',
    description: 'Find even, natural light facing you',
  },
]

function GuideScreen({
  onOpenCamera,
  onSelectFile,
  onTestMode,
  error,
}: {
  onOpenCamera: () => void
  onSelectFile: () => void
  onTestMode: () => void
  error: string | null
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex-1 flex flex-col bg-gradient-to-b from-[#FFF9F5] via-[#FFFEF1] to-white relative"
    >
      {/* Decorative sparkles */}
      <Sparkle className="absolute top-8 left-6" size={16} color="#FF8C33" delay={0} />
      <Sparkle className="absolute top-16 right-8" size={12} color="#F596FA" delay={0.5} />
      <Sparkle className="absolute top-32 left-12" size={14} color="#7182F8" delay={1} />

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col items-center px-4 py-6">
          {/* Header with camera emoji */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-center mb-6"
          >
            {/* Camera emoji with animation */}
            <motion.div
              className="text-6xl mb-4"
              animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              📸
            </motion.div>

            <h2
              className="text-2xl font-bold text-[#075D61] mb-1"
              style={{ fontFamily: "'Anybody', sans-serif" }}
            >
              Time for a Selfie!
            </h2>
            <p className="text-gray-500 text-sm">Take a photo and we'll analyze your skin</p>
          </motion.div>

          {/* Tips card - clean white card design */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="w-full max-w-sm bg-white rounded-2xl shadow-lg border border-gray-100 p-5 mb-4"
          >
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-3 font-medium">Quick Tips</p>
            <div className="space-y-3">
              {PHOTO_TIPS.map((tip, index) => (
                <motion.div
                  key={tip.title}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-9 h-9 rounded-full bg-[#46C0C6]/10 flex items-center justify-center shrink-0">
                    <span className="text-base">{tip.icon}</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800 text-sm">{tip.title}</p>
                    <p className="text-gray-500 text-xs">{tip.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Test mode button for UX testing without API calls */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            onClick={onTestMode}
            className="mb-4 text-sm text-teal-600 underline decoration-teal-400 decoration-2 underline-offset-2 bg-teal-50 px-3 py-1.5 rounded-full hover:bg-teal-100 transition-colors"
          >
            🧪 Test with Shane's Face!
          </motion.button>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 max-w-sm"
            >
              <p className="text-red-700 text-sm whitespace-pre-wrap">{error}</p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Fixed buttons at bottom with safe area padding */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="shrink-0 bg-white/90 backdrop-blur-sm border-t border-gray-100 px-4 pt-4 pb-8 safe-bottom"
        style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom, 2rem))' }}
      >
        <div className="w-full max-w-sm mx-auto space-y-3">
          <Button size="lg" fullWidth onClick={onOpenCamera} className="shadow-md">
            Take Selfie
          </Button>
          <Button variant="outline" size="md" fullWidth onClick={onSelectFile}>
            Choose from Gallery
          </Button>
        </div>
      </motion.div>
    </motion.div>
  )
}

function CropScreen({
  image,
  crop,
  zoom,
  rotation,
  onCropChange,
  onZoomChange,
  onRotationChange,
  onCropComplete,
  onConfirm,
  onRetake,
}: {
  image: string
  crop: { x: number; y: number }
  zoom: number
  rotation: number
  onCropChange: (crop: { x: number; y: number }) => void
  onZoomChange: (zoom: number) => void
  onRotationChange: (rotation: number) => void
  onCropComplete: (croppedArea: Area, croppedAreaPixels: Area) => void
  onConfirm: () => void
  onRetake: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex-1 flex flex-col"
    >
      {/* Cropper - uses responsive crop area based on container */}
      <div className="flex-1 relative bg-[#1a1a1a]">
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={3 / 4}
          cropShape="round"
          showGrid={false}
          onCropChange={onCropChange}
          onZoomChange={onZoomChange}
          onRotationChange={onRotationChange}
          onCropComplete={onCropComplete}
          minZoom={0.5}
          maxZoom={3}
          objectFit="contain"
          style={{
            containerStyle: { background: '#1a1a1a' },
            cropAreaStyle: { border: '3px solid #46C0C6' },
          }}
        />
      </div>

      {/* Instructions */}
      <div className="bg-[#1a1a1a] px-6 py-3">
        <p className="text-white/60 text-sm text-center flex items-center justify-center gap-2">
          <span>👆</span>
          <span>Pinch to zoom • Drag to position</span>
        </p>
      </div>

      {/* Actions - compact footer with extra bottom padding for browser nav bars */}
      <div className="bg-white border-t border-gray-100 px-4 pt-3 pb-20 safe-bottom">
        <div className="max-w-sm mx-auto flex gap-3">
          <Button variant="secondary" size="md" onClick={onRetake} className="shrink-0">
            Retake
          </Button>
          <Button size="lg" fullWidth onClick={onConfirm} className="shadow-md">
            Confirm
          </Button>
        </div>
      </div>
    </motion.div>
  )
}

function PreviewScreen({
  photoData,
  onRetake,
  onAdjust,
  onContinue,
  error,
  continueButtonText = 'Analyze My Skin',
}: {
  photoData: string
  onRetake: () => void
  onAdjust?: () => void  // Optional - not available on iOS
  onContinue: () => void
  error?: string | null
  continueButtonText?: string
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex-1 flex flex-col overflow-hidden bg-gradient-to-b from-[#FFF5F0] via-[#F0F9F9] to-white relative"
    >
      {/* Decorative sparkles */}
      <motion.svg
        className="absolute top-16 left-6 z-0"
        width={16}
        height={16}
        viewBox="0 0 24 24"
        fill="#FF8C33"
        animate={{ scale: [1, 1.3, 1], opacity: [0.6, 1, 0.6], rotate: [0, 15, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
      </motion.svg>
      <motion.svg
        className="absolute top-24 right-8 z-0"
        width={12}
        height={12}
        viewBox="0 0 24 24"
        fill="#46C0C6"
        animate={{ scale: [1, 1.3, 1], opacity: [0.6, 1, 0.6], rotate: [0, 15, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      >
        <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
      </motion.svg>
      <motion.svg
        className="absolute bottom-40 left-4 z-0"
        width={14}
        height={14}
        viewBox="0 0 24 24"
        fill="#F596FA"
        animate={{ scale: [1, 1.3, 1], opacity: [0.6, 1, 0.6], rotate: [0, 15, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      >
        <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
      </motion.svg>
      <motion.svg
        className="absolute bottom-60 right-6 z-0"
        width={10}
        height={10}
        viewBox="0 0 24 24"
        fill="#7182F8"
        animate={{ scale: [1, 1.3, 1], opacity: [0.6, 1, 0.6], rotate: [0, 15, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
      >
        <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
      </motion.svg>

      {/* Floating circles in background */}
      <motion.div
        className="absolute top-20 right-12 w-20 h-20 rounded-full bg-[#46C0C6]/10 z-0"
        animate={{ y: [0, -10, 0], scale: [1, 1.05, 1] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-48 left-6 w-16 h-16 rounded-full bg-[#FF8C33]/10 z-0"
        animate={{ y: [0, 10, 0], scale: [1, 1.05, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />

      {/* Error message if present */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="shrink-0 bg-red-50 border border-red-200 rounded-xl p-4 m-4 relative z-10"
        >
          <p className="text-red-700 text-sm whitespace-pre-wrap">{error}</p>
        </motion.div>
      )}

      {/* Photo preview area with card styling */}
      <div className="flex-1 relative flex items-center justify-center p-6 min-h-0 z-10">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', damping: 20 }}
          className="relative"
        >
          {/* Photo card with shadow */}
          <div
            className="relative rounded-3xl overflow-hidden shadow-xl aspect-[3/4] border-4 border-white"
            style={{
              width: 'min(70vw, 280px)',
              maxHeight: '70vh'
            }}
          >
            <img
              src={photoData}
              alt="Your photo"
              className="w-full h-full object-cover object-center"
            />
          </div>

          {/* Success badge */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, type: 'spring' }}
            className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#46C0C6] to-[#7182F8] text-white px-5 py-2 rounded-full text-sm font-semibold shadow-lg flex items-center gap-2"
          >
            <span className="text-base">👍</span>
            Looking Good?
          </motion.div>
        </motion.div>
      </div>

      {/* Actions - fixed footer with extra bottom padding for browser nav bars */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="shrink-0 bg-white/80 backdrop-blur-sm border-t border-gray-100 px-4 pt-4 pb-20 safe-bottom"
      >
        <div className="max-w-sm mx-auto">
          {onAdjust && (
            <p className="text-center text-gray-500 text-sm mb-3">
              <button onClick={onAdjust} className="text-[#46C0C6] underline">
                Reposition your face
              </button>
            </p>
          )}
          <div className="flex gap-3">
            <Button variant="secondary" size="md" onClick={onRetake} className="shrink-0">
              Retake
            </Button>
            <Button size="lg" fullWidth onClick={onContinue} className="shadow-md">
              {continueButtonText}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

// Helper function to crop the image with rotation support
// Returns Blob to avoid iOS Safari data URL limits
// PerfectCorp HD analysis requires minimum 1536px height for reliable results
async function getCroppedImg(imageSrc: string, pixelCrop: Area, rotation = 0): Promise<Blob> {
  debugLog('[getCroppedImg] Crop area (pixels):', pixelCrop.width, 'x', pixelCrop.height, 'at', pixelCrop.x, ',', pixelCrop.y, 'rotation:', rotation)

  const image = await createImage(imageSrc)
  debugLog('[getCroppedImg] Source image:', image.width, 'x', image.height)

  // PerfectCorp requires minimum 1536px height for HD analysis
  const MIN_HEIGHT = 1536
  const MIN_WIDTH = 1152 // 1536 * 0.75 (3:4 aspect ratio)

  let outputWidth = pixelCrop.width
  let outputHeight = pixelCrop.height

  // Only upscale if the cropped area is smaller than minimum
  if (pixelCrop.height < MIN_HEIGHT) {
    const scaleFactor = MIN_HEIGHT / pixelCrop.height
    outputHeight = MIN_HEIGHT
    outputWidth = Math.round(pixelCrop.width * scaleFactor)
    debugLog('[getCroppedImg] Upscaling by factor:', scaleFactor.toFixed(2))
  }

  // Ensure minimum width as well
  if (outputWidth < MIN_WIDTH) {
    const scaleFactor = MIN_WIDTH / outputWidth
    outputWidth = MIN_WIDTH
    outputHeight = Math.round(outputHeight * scaleFactor)
    debugLog('[getCroppedImg] Width upscale by factor:', scaleFactor.toFixed(2))
  }

  debugLog('[getCroppedImg] Output size:', outputWidth, 'x', outputHeight)

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('No 2d context')
  }

  // If no rotation, use simple crop
  if (rotation === 0) {
    canvas.width = outputWidth
    canvas.height = outputHeight
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      outputWidth,
      outputHeight
    )
  } else {
    // With rotation: need to draw rotated image first, then crop
    const radians = (rotation * Math.PI) / 180

    // Calculate the bounding box of the rotated image
    const sin = Math.abs(Math.sin(radians))
    const cos = Math.abs(Math.cos(radians))
    const rotatedWidth = image.width * cos + image.height * sin
    const rotatedHeight = image.width * sin + image.height * cos

    // Create a temporary canvas for the rotated image
    const rotatedCanvas = document.createElement('canvas')
    rotatedCanvas.width = rotatedWidth
    rotatedCanvas.height = rotatedHeight
    const rotatedCtx = rotatedCanvas.getContext('2d')

    if (!rotatedCtx) {
      throw new Error('No 2d context for rotation')
    }

    // Draw rotated image
    rotatedCtx.translate(rotatedWidth / 2, rotatedHeight / 2)
    rotatedCtx.rotate(radians)
    rotatedCtx.drawImage(image, -image.width / 2, -image.height / 2)

    // Now crop from the rotated canvas
    canvas.width = outputWidth
    canvas.height = outputHeight
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    // Adjust crop coordinates for the rotated canvas center offset
    const offsetX = (rotatedWidth - image.width) / 2
    const offsetY = (rotatedHeight - image.height) / 2

    ctx.drawImage(
      rotatedCanvas,
      pixelCrop.x + offsetX,
      pixelCrop.y + offsetY,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      outputWidth,
      outputHeight
    )
  }

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob from canvas'))
          return
        }
        debugLog('[getCroppedImg] Created blob, size:', Math.round(blob.size / 1024), 'KB')
        resolve(blob)
      },
      'image/jpeg',
      0.95
    )
  })
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    image.src = url
  })
}

/**
 * Convert a Blob to a base64 data URL
 * This is necessary for persistent storage - blob URLs get revoked when navigating away
 */
function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
      } else {
        reject(new Error('Failed to convert blob to data URL'))
      }
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * Resize an image if it exceeds the maximum dimension
 * This is crucial for iOS Safari which has canvas memory limits
 * Uses only Blob URLs and toBlob() to avoid data URL size limits
 */
async function resizeImageIfNeeded(imageBlob: Blob, maxDimension: number): Promise<Blob> {
  debugLog('[resizeIfNeeded] Starting with blob size:', Math.round(imageBlob.size / 1024), 'KB')

  // Create a Blob URL to load the image
  let blobUrl: string
  try {
    blobUrl = URL.createObjectURL(imageBlob)
    debugLog('[resizeIfNeeded] Created blob URL:', blobUrl.substring(0, 60))
  } catch (urlError) {
    console.error('[resizeIfNeeded] FAILED to create blob URL:', urlError)
    throw urlError
  }

  try {
    debugLog('[resizeIfNeeded] Loading image from blob URL...')
    const image = await createImage(blobUrl)
    debugLog('[resizeIfNeeded] Image loaded! Original:', image.width, 'x', image.height)

    // Check if resizing is needed
    if (image.width <= maxDimension && image.height <= maxDimension) {
      debugLog('[resizeIfNeeded] No resize needed')
      return imageBlob
    }

    // Calculate new dimensions maintaining aspect ratio
    let newWidth: number
    let newHeight: number

    if (image.width > image.height) {
      newWidth = maxDimension
      newHeight = Math.round((image.height / image.width) * maxDimension)
    } else {
      newHeight = maxDimension
      newWidth = Math.round((image.width / image.height) * maxDimension)
    }

    debugLog('[resizeIfNeeded] Resizing to:', newWidth, 'x', newHeight)

    // Create canvas and resize
    const canvas = document.createElement('canvas')
    canvas.width = newWidth
    canvas.height = newHeight

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      console.error('[resizeIfNeeded] Failed to get canvas context')
      return imageBlob
    }

    // Use high-quality scaling
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    ctx.drawImage(image, 0, 0, newWidth, newHeight)

    // Convert to Blob (NOT data URL!)
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob from resized canvas'))
            return
          }
          debugLog('[resizeIfNeeded] Resized blob size:', Math.round(blob.size / 1024), 'KB')
          resolve(blob)
        },
        'image/jpeg',
        0.92 // Slightly higher quality since we're downsizing
      )
    })
  } finally {
    // Always clean up the Blob URL
    URL.revokeObjectURL(blobUrl)
  }
}

// NOTE: The following functions were removed as they used data URLs which cause
// "String did not match expected pattern" on iOS Safari with large images:
// - dataURLtoBlob
// - chunkedBase64ToBlob
// - ensureMinimumResolution (replaced by ensureMinimumResolutionBlob)

/**
 * Resize an image safely for iOS Safari
 * This function uses createImageBitmap when available, which is more memory-efficient
 * and bypasses many of iOS Safari's rendering limits
 */
async function resizeImageForIOSSafely(imageBlob: Blob, maxDimension: number): Promise<Blob> {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  debugLog('[resizeIOS] Starting, blob size:', Math.round(imageBlob.size / 1024), 'KB, isIOS:', isIOS)

  // Try to use createImageBitmap for more efficient processing
  // This API works directly with Blobs and doesn't have the same limits as img.src
  // IMPORTANT: createImageBitmap is supported on iOS Safari 15+
  if (typeof createImageBitmap === 'function') {
    try {
      debugLog('[resizeIOS] Step 1: Calling createImageBitmap...')
      const bitmap = await createImageBitmap(imageBlob)
      const { width, height } = bitmap
      debugLog('[resizeIOS] Step 2: Bitmap created, dimensions:', width, 'x', height)

      // Check if resize is needed
      if (width <= maxDimension && height <= maxDimension) {
        debugLog('[resizeIOS] No resize needed, returning original')
        bitmap.close()
        return imageBlob
      }

      // Calculate new dimensions
      let newWidth: number
      let newHeight: number
      if (width > height) {
        newWidth = maxDimension
        newHeight = Math.round((height / width) * maxDimension)
      } else {
        newHeight = maxDimension
        newWidth = Math.round((width / height) * maxDimension)
      }

      debugLog('[resizeIOS] Step 3: Creating canvas', newWidth, 'x', newHeight)

      // Create canvas and draw the bitmap (more efficient than drawImage with img element)
      const canvas = document.createElement('canvas')
      canvas.width = newWidth
      canvas.height = newHeight

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        bitmap.close()
        throw new Error('Failed to get canvas context')
      }

      debugLog('[resizeIOS] Step 4: Drawing bitmap to canvas...')
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(bitmap, 0, 0, newWidth, newHeight)
      bitmap.close()
      debugLog('[resizeIOS] Step 5: Draw complete, converting to blob...')

      // Convert to blob
      return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('toBlob failed'))
              return
            }
            debugLog('[resizeIOS] Step 6: SUCCESS! Resized blob size:', Math.round(blob.size / 1024), 'KB')
            resolve(blob)
          },
          'image/jpeg',
          0.92
        )
      })
    } catch (bitmapError) {
      const errName = bitmapError instanceof Error ? bitmapError.name : 'Unknown'
      const errMsg = bitmapError instanceof Error ? bitmapError.message : String(bitmapError)
      console.error('[resizeIOS] createImageBitmap FAILED:', errName, errMsg)
      console.error('[resizeIOS] Full error:', bitmapError)

      // On iOS, if createImageBitmap fails, DON'T fall back to legacy method
      // The legacy method uses Image.src which has the same issue on iOS
      // Just return the original blob - iOS camera images are usually within size limits
      if (isIOS) {
        debugLog('[resizeIOS] iOS: Returning original blob after createImageBitmap failure')
        return imageBlob
      }
      // Fall through to legacy method only on non-iOS
    }
  } else {
    debugLog('[resizeIOS] createImageBitmap not available')
    // On iOS without createImageBitmap support (very old iOS), return original
    if (isIOS) {
      debugLog('[resizeIOS] iOS without createImageBitmap: returning original blob')
      return imageBlob
    }
  }

  // Fallback: Use traditional Image element - ONLY for non-iOS
  // iOS Safari has issues with Image.src and large blobs
  debugLog('[resizeIOS] Using fallback Image element method (non-iOS)')
  return resizeImageIfNeeded(imageBlob, maxDimension)
}

// NOTE: ensureMinimumResolutionBlob was removed as it's now handled in getCroppedImg
